<template>
  <section class="v2-profile-section">
    <header class="v2-profile-section__header">
      <div class="v2-profile-section__title-wrap">
        <component
          :is="isPrimary ? 'h2' : 'h3'"
          :class="[
            'v2-headline',
            isPrimary ? 'v2-headline--display-md' : 'v2-headline--display-sm',
            'v2-profile-section__title'
          ]"
        >
          {{ section.name }}
        </component>
        <p
          v-if="section.description"
          class="v2-body v2-body--sm v2-body--muted v2-profile-section__desc"
        >
          {{ section.description }}
        </p>
      </div>
      <div v-if="!readOnly" class="v2-profile-section__actions">
        <BaseButton
          v-if="!editing"
          variant="amber"
          size="md"
          @click="$emit('edit')"
        >
          <template #icon-left>
            <v-icon size="16">mdi-pencil-outline</v-icon>
          </template>
          Editar
        </BaseButton>
        <template v-else>
          <BaseButton
            variant="ghost"
            size="sm"
            :disabled="saving"
            @click="$emit('cancel')"
          >
            Cancelar
          </BaseButton>
          <BaseButton
            variant="primary"
            size="sm"
            :loading="saving"
            :disabled="saving"
            @click="$emit('save')"
          >
            <template #icon-left>
              <v-icon size="16">mdi-content-save</v-icon>
            </template>
            Guardar
          </BaseButton>
        </template>
      </div>
    </header>

    <div :class="['v2-profile-section__grid', { 'v2-profile-section__grid--editing': editing }]">
      <ProfileField
        v-for="field in visibleFields"
        :key="field._renderKey || `${field.key || field.formattedName || 'field'}-${field._instanceKey || ''}`"
        :field="field"
        :editing="editing"
        :error="fieldError(field)"
        @update="onFieldUpdate"
      />
    </div>
  </section>
</template>

<script>
import BaseButton from "@/components/memberDashboardV2/ui/BaseButton.vue";
import ProfileField from "./ProfileField.vue";
import { expandFieldInstances } from "./profileHelpers";

export default {
  name: "ProfileSection",
  components: { BaseButton, ProfileField },
  props: {
    section: { type: Object, required: true },
    editing: { type: Boolean, default: false },
    saving: { type: Boolean, default: false },
    readOnly: { type: Boolean, default: false },
    errors: { type: Object, default: () => ({}) },
  },
  computed: {
    isPrimary() {
      return this.section.id === "basic";
    },
    visibleFields() {
      // Expand conditional children and keep only those whose parent
      // actually has a value at the moment of render.
      const out = [];
      this.section.fields.forEach((field) => {
        const instances = expandFieldInstances(field, this.section.fields);
        instances.forEach((instance) => {
          if (instances.length > 1) {
            instance._renderKey = `${field.key || field.formattedName}-${instance._instanceKey}`;
          } else {
            instance._renderKey = field.key || field.formattedName;
          }
          out.push(instance);
        });
      });
      return out;
    },
  },
  methods: {
    fieldError(field) {
      const key = field.key || field.formattedName;
      return this.errors[key] || "";
    },
    onFieldUpdate(payload) {
      this.$emit("field-update", {
        ...payload,
        sectionId: this.section.id,
      });
    },
  },
};
</script>

<style scoped>
.v2-profile-section {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  background-color: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xl);
  padding: var(--space-6);
}

.v2-profile-section__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  border-bottom: 1px solid var(--color-border);
  padding-bottom: var(--space-3);
  flex-wrap: wrap;
}

.v2-profile-section__title-wrap {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  min-width: 0;
  flex: 1 1 auto;
}

.v2-profile-section__title {
  margin: 0;
}

.v2-profile-section__desc {
  margin: 0;
}

.v2-profile-section__actions {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  flex-shrink: 0;
}

.v2-profile-section__grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--space-4) var(--space-6);
  background-color: var(--color-neutral-100);
  padding: var(--space-4);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border);
}

@media (min-width: 768px) {
  .v2-profile-section__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (min-width: 1024px) {
  .v2-profile-section__grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.v2-profile-section__grid--editing {
  background-color: var(--color-amber-soft-2);
  border-color: var(--color-amber);
  padding: var(--space-4);
  border-radius: var(--radius-md);
}
</style>